"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE =
	"/image/new-version/ValueAddedSolutions/industrial-automation/phased-full-cycle-project-escort";

const STAGES = [
	{
		keyPrefix: "projectEscortStage1",
		image: `${IMAGE_BASE}/initial-stage.webp`,
		featureCount: 5,
	},
	{
		keyPrefix: "projectEscortStage2",
		image: `${IMAGE_BASE}/middle-stage.webp`,
		featureCount: 6,
	},
	{
		keyPrefix: "projectEscortStage3",
		image: `${IMAGE_BASE}/final-stage.webp`,
		featureCount: 4,
	},
] as const;

const STAGE_POSITIONS = ["18%", "52%", "86%"] as const;
const PROGRESS_HEIGHTS = ["24%", "58%", "100%"] as const;

const CheckIcon = () => (
	<span
		className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 text-white"
		aria-hidden="true"
	>
		<svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
			<path
				d="M3.5 8.3 6.4 11 12.5 5"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	</span>
);

export default function IndustrialAutomationProjectEscort() {
	const t = useTranslations("new-industrial-automation");
	const [activeIndex, setActiveIndex] = useState(0);
	const activeStage = STAGES[activeIndex];

	const features = useMemo(
		() =>
			Array.from({ length: activeStage.featureCount }, (_, index) => ({
				titleKey: `${activeStage.keyPrefix}Feature${index + 1}Title`,
				descKey: `${activeStage.keyPrefix}Feature${index + 1}Desc`,
			})),
		[activeStage],
	);

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("projectEscortTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("projectEscortDesc")}</p>
				</header>

				<div className="grid gap-8 lg:grid-cols-[170px_1fr] lg:gap-10">
					<div className="relative flex gap-3 overflow-x-auto pb-2 lg:min-h-[520px] lg:overflow-visible lg:pb-0">
						<span
							className="absolute right-4 top-0 hidden h-full w-4 rounded-full bg-[#f5ffd4] lg:block"
							aria-hidden="true"
						/>
						<span
							className="absolute right-4 top-0 hidden w-4 rounded-full bg-themePrimary400 transition-all duration-300 lg:block"
							style={{ height: PROGRESS_HEIGHTS[activeIndex] }}
							aria-hidden="true"
						/>
						<span
							className="absolute right-[10px] hidden h-7 w-7 -translate-y-1/2 rounded-full bg-themePrimary400 transition-all duration-300 lg:block"
							style={{ top: PROGRESS_HEIGHTS[activeIndex] }}
							aria-hidden="true"
						/>
						{STAGES.map((stage, index) => (
							<button
								key={stage.keyPrefix}
								type="button"
								onClick={() => setActiveIndex(index)}
								className={`relative z-10 flex min-w-fit items-center gap-3 rounded-full px-4 py-2 text-sm font-semibold transition-colors lg:absolute lg:left-0 lg:w-[130px] lg:-translate-y-1/2 lg:justify-start lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0 lg:text-base ${
									activeIndex === index
										? "bg-[#e9ffd6] text-themePrimary400"
										: "bg-[#f5f7fa] text-gray-300 lg:bg-transparent"
								}`}
								style={{ top: STAGE_POSITIONS[index] }}
							>
								<span className="h-2 w-2 rounded-full bg-current lg:hidden" aria-hidden="true" />
								{t(`${stage.keyPrefix}Label`)}
							</button>
						))}
					</div>

					<div>
						<div className="grid gap-6 md:grid-cols-[0.95fr_1.05fr] md:items-start md:gap-10">
							<div>
								<p className="mb-5 inline-flex bg-[#f3ffe8] px-4 py-2 text-base font-bold text-themePrimary400 sm:text-lg">
									{t(`${activeStage.keyPrefix}Title`)}
								</p>
								<p className="text-sm leading-relaxed text-black md:text-base">
									{t(`${activeStage.keyPrefix}Desc`)}
								</p>
							</div>
							<div className="relative aspect-[16/9] overflow-hidden rounded-lg bg-[#f5f7fa]">
								<Image
									src={activeStage.image}
									alt={t(`${activeStage.keyPrefix}ImageAlt`)}
									fill
									className="object-cover"
									sizes="(max-width: 768px) 100vw, 50vw"
								/>
							</div>
						</div>

						<div className="mt-8">
							<h3 className="mb-6 text-lg font-bold text-black sm:text-xl md:text-2xl">
								{t("projectEscortExpertSupport")}
							</h3>
							<div className="grid gap-5 md:grid-cols-2 md:gap-x-12 md:gap-y-7">
								{features.map((feature) => (
									<div key={feature.titleKey} className="flex gap-3">
										<CheckIcon />
										<div>
											<h4 className="mb-1 text-base font-bold text-black sm:text-lg">
												{t(feature.titleKey)}
											</h4>
											<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
										</div>
									</div>
								))}
							</div>
						</div>

						<Link
							href="/contact-us"
							className="mt-8 inline-flex items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#83b751] hover:text-white md:px-8 md:py-3.5 md:text-base"
						>
							{t("projectEscortContact")}
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
}
